import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMutation } from "@tanstack/react-query";
// Import client configuration first to ensure interceptors are loaded
import "../api/client";
import {
  loginApiAuthLoginPost,
  refreshTokenApiAuthRefreshPost,
  logoutApiAuthLogoutPost,
} from "../api/generated";
import type { TokenResponse } from "../api/generated";
import {
  storeTokens,
  getTokens,
  clearTokens,
  decodeToken,
  getRefreshDelay,
} from "../utils/auth";
import type { UserResponse } from "../api/generated/types.gen";
import { AuthState } from "../api/types";

// Alias for convenience
type User = UserResponse;

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clear any existing timer
  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, [refreshTimerRef]);

  // Start refresh timer based on token expiration
  const startRefreshTimer = useCallback(
    (exp: number) => {
      clearRefreshTimer();
      const delay = getRefreshDelay(exp);

      if (delay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          refreshTokenMutation.mutate();
        }, delay);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearRefreshTimer]
  );

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: {
      email: string;
      password: string;
    }): Promise<TokenResponse> => {
      const response = await loginApiAuthLoginPost({
        body: credentials,
      });

      if (response.error) {
        const detail = response.error.detail;
        const message = typeof detail === "string" ? detail : "Login failed";
        throw new Error(message);
      }

      return response.data!;
    },
    onSuccess: (data) => {
      storeTokens(data.access_token, data.refresh_token);
      const { user, exp } = decodeToken(data.access_token);

      setAuthState({
        user: {
          ...user,
          full_name: data.user.full_name,
          is_active: data.user.is_active,
          created_at: data.user.created_at,
          updated_at: data.user.updated_at,
        } as User,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      startRefreshTimer(exp);
    },
    onError: (error: Error) => {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.message,
      });
    },
  });

  // Refresh token mutation
  const refreshTokenMutation = useMutation({
    mutationFn: async (): Promise<TokenResponse> => {
      const { refreshToken } = getTokens();

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await refreshTokenApiAuthRefreshPost({
        body: { refresh_token: refreshToken },
      });

      if (response.error) {
        const detail = response.error.detail;
        const message =
          typeof detail === "string" ? detail : "Token refresh failed";
        throw new Error(message);
      }

      return response.data!;
    },
    onSuccess: (data) => {
      storeTokens(data.access_token, data.refresh_token);
      const { user, exp } = decodeToken(data.access_token);

      setAuthState({
        user: {
          ...user,
          full_name: data.user.full_name,
          is_active: data.user.is_active,
          created_at: data.user.created_at,
          updated_at: data.user.updated_at,
        } as User,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      startRefreshTimer(exp);
    },
    onError: () => {
      // Refresh failed - logout user
      clearTokens();
      clearRefreshTimer();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: "Session expired. Please login again.",
      });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const { accessToken } = getTokens();

      if (accessToken) {
        await logoutApiAuthLogoutPost();
      }
    },
    onSettled: () => {
      clearTokens();
      clearRefreshTimer();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    },
  });

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      // Development mode: auto-authenticate with mock admin user
      const isDevelopment =
        process.env.NODE_ENV === "development" ||
        process.env.REACT_APP_DISABLE_AUTH === "true";

      if (isDevelopment) {
        // Create a mock admin user for development
        const mockUser: User = {
          id: "dev-admin",
          email: "admin@dev.local",
          full_name: "Development Admin",
          role: "admin" as const,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setAuthState({
          user: mockUser,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        return;
      }

      // Production mode: check for valid token
      const { accessToken } = getTokens();

      if (accessToken) {
        try {
          const { user, exp } = decodeToken(accessToken);

          // Check if token is still valid
          if (exp * 1000 > Date.now()) {
            setAuthState({
              user: user as User,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            startRefreshTimer(exp);
          } else {
            // Token expired - try to refresh
            refreshTokenMutation.mutate();
          }
        } catch (error) {
          // Invalid token - clear storage
          clearTokens();
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    initializeAuth();

    // Cleanup timer on unmount
    return () => clearRefreshTimer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMutation.mutateAsync({ email, password });
    },
    [loginMutation]
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const refreshToken = useCallback(async () => {
    await refreshTokenMutation.mutateAsync();
  }, [refreshTokenMutation]);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
